import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { EffectiveConfiguration } from '../../app/services/api/generated';
import { ConfigurationName } from '../../enums/ConfigurationName';
import useNswagClient from '../../hooks/api/useNswagClient';
import { UserContext } from './useUser';

export type ConfigurationLoadStatus = 'idle' | 'loading' | 'ready' | 'error';

interface ConfigurationSnapshot {
  status: ConfigurationLoadStatus;
  storeNumber?: string;
  values: Record<string, string>;
  error?: Error;
}

interface ConfigurationContextType {
  activeStatus: ConfigurationLoadStatus;
  activeError?: Error;
  getConfigurationValue: (name: ConfigurationName) => string | undefined;
  isConfigurationEnabled: (name: ConfigurationName) => boolean;
  loadConfigurationsForStore: (storeNumber: string) => Promise<Record<string, string>>;
  retryConfigurations: () => Promise<void>;
  invalidateConfigurations: () => Promise<void>;
}

interface ConfigurationContextProviderProps {
  children: ReactNode;
}

const validConfigurationNames = new Set(
  Object.keys(ConfigurationName).filter(name => Number.isNaN(Number(name))),
);

const createSnapshot = (configurations: EffectiveConfiguration[] | undefined): Record<string, string> => {
  return (configurations ?? []).reduce<Record<string, string>>((snapshot, configuration) => {
    if (
      configuration.name
      && validConfigurationNames.has(configuration.name)
      && typeof configuration.value === 'string'
    ) {
      snapshot[configuration.name] = configuration.value;
    }

    return snapshot;
  }, {});
};

const ConfigurationContext = createContext<ConfigurationContextType | undefined>(undefined);

export const ConfigurationContextProvider = ({ children }: ConfigurationContextProviderProps) => {
  const { selectedStore } = useContext(UserContext);
  const { isLoaded, isSignedIn } = useAuth();
  const { getEffectiveConfigurations } = useNswagClient();
  const getEffectiveConfigurationsRef = useRef(getEffectiveConfigurations);
  getEffectiveConfigurationsRef.current = getEffectiveConfigurations;

  const [activeConfiguration, setActiveConfiguration] = useState<ConfigurationSnapshot>({
    status: 'idle',
    values: {},
  });
  const activeConfigurationRef = useRef(activeConfiguration);
  const activeRequestRef = useRef<Promise<void> | undefined>();
  const requestVersionRef = useRef(0);

  const publishActiveConfiguration = useCallback((snapshot: ConfigurationSnapshot): void => {
    activeConfigurationRef.current = snapshot;
    setActiveConfiguration(snapshot);
  }, []);

  const resetActiveConfiguration = useCallback((): void => {
    requestVersionRef.current += 1;
    activeRequestRef.current = undefined;
    publishActiveConfiguration({ status: 'idle', values: {}, storeNumber: undefined });
  }, [publishActiveConfiguration]);

  const fetchConfigurationValues = useCallback((storeNumber: string): Promise<Record<string, string>> => {
    if (!storeNumber) {
      return Promise.resolve({});
    }

    return getEffectiveConfigurationsRef.current(storeNumber)
      .then(response => createSnapshot(response.data?.configurations));
  }, []);

  const loadActiveConfigurations = useCallback((storeNumber: string): Promise<void> => {
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    publishActiveConfiguration({ status: 'loading', values: {}, storeNumber });

    const request = fetchConfigurationValues(storeNumber)
      .then(values => {
        if (requestVersionRef.current === requestVersion) {
          publishActiveConfiguration({ status: 'ready', values, storeNumber });
        }
      })
      .catch(error => {
        if (requestVersionRef.current === requestVersion) {
          publishActiveConfiguration({
            status: 'error',
            values: {},
            storeNumber,
            error: error instanceof Error ? error : new Error('Failed to load configurations'),
          });
        }

        throw error;
      })
      .finally(() => {
        if (activeRequestRef.current === request) {
          activeRequestRef.current = undefined;
        }
      });

    activeRequestRef.current = request;
    return request;
  }, [fetchConfigurationValues, publishActiveConfiguration]);

  useEffect(() => {
    const storeNumber = selectedStore?.storeNumber;
    if (!isLoaded) {
      return;
    }

    if (!isSignedIn || !storeNumber) {
      resetActiveConfiguration();
      return;
    }

    // lazy: skip when a load for this store is already in flight or done, e.g. a child
    // effect called loadConfigurationsForStore first (child effects run before ours).
    const snapshot = activeConfigurationRef.current;
    if (snapshot.storeNumber === storeNumber && (snapshot.status === 'loading' || snapshot.status === 'ready')) {
      return;
    }

    void loadActiveConfigurations(storeNumber).catch(() => undefined);
  }, [isLoaded, isSignedIn, loadActiveConfigurations, resetActiveConfiguration, selectedStore?.storeNumber]);

  const getConfigurationValue = useCallback((name: ConfigurationName): string | undefined => {
    const snapshot = activeConfigurationRef.current;
    if (snapshot.storeNumber !== selectedStore?.storeNumber || snapshot.status !== 'ready') {
      return undefined;
    }

    return snapshot.values[ConfigurationName[name]];
  }, [selectedStore?.storeNumber]);

  const isConfigurationEnabled = useCallback((name: ConfigurationName): boolean => {
    return getConfigurationValue(name) === 'true';
  }, [getConfigurationValue]);

  const loadConfigurationsForStore = useCallback(async (storeNumber: string): Promise<Record<string, string>> => {
    if (storeNumber === selectedStore?.storeNumber) {
      const snapshot = activeConfigurationRef.current;
      if (snapshot.status === 'ready' && snapshot.storeNumber === storeNumber) {
        return snapshot.values;
      }

      if (activeRequestRef.current) {
        await activeRequestRef.current;
      } else {
        await loadActiveConfigurations(storeNumber);
      }

      const loadedSnapshot = activeConfigurationRef.current;
      return loadedSnapshot.storeNumber === storeNumber ? loadedSnapshot.values : {};
    }

    return fetchConfigurationValues(storeNumber);
  }, [fetchConfigurationValues, loadActiveConfigurations, selectedStore?.storeNumber]);

  const retryConfigurations = useCallback((): Promise<void> => {
    const storeNumber = selectedStore?.storeNumber;
    return storeNumber ? loadActiveConfigurations(storeNumber) : Promise.resolve();
  }, [loadActiveConfigurations, selectedStore?.storeNumber]);

  const invalidateConfigurations = useCallback(async (): Promise<void> => {
    const storeNumber = selectedStore?.storeNumber;
    if (storeNumber) {
      await loadActiveConfigurations(storeNumber);
    } else {
      resetActiveConfiguration();
    }
  }, [loadActiveConfigurations, resetActiveConfiguration, selectedStore?.storeNumber]);

  const value = useMemo<ConfigurationContextType>(() => ({
    activeStatus: activeConfiguration.status,
    activeError: activeConfiguration.error,
    getConfigurationValue,
    isConfigurationEnabled,
    loadConfigurationsForStore,
    retryConfigurations,
    invalidateConfigurations,
  }), [
    activeConfiguration,
    getConfigurationValue,
    invalidateConfigurations,
    isConfigurationEnabled,
    loadConfigurationsForStore,
    retryConfigurations,
  ]);

  return (
    <ConfigurationContext.Provider value={value}>
      {children}
    </ConfigurationContext.Provider>
  );
};

export const useConfigurations = (): ConfigurationContextType => {
  const context = useContext(ConfigurationContext);
  if (!context) {
    throw new Error('useConfigurations must be used within a ConfigurationContextProvider');
  }

  return context;
};
